/* eslint-disable no-underscore-dangle */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const CDN_BASE = 'https://quark.uacdn.net/calculator_v10/Calc';
const CDN_FILES = `${CDN_BASE}/SCIENTIFIC%20CALCULATOR_files`;
const CSS_ID = 'ua-calculator-css';
const ROOT_CLASS = 'ua-calculator-root';

const Root = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

let jqueryPromise = null;
let cssPromise = null;
const markupCache = {};

const scopeCalculatorCss = (css) =>
  css
    .replace(/\*\s*\{/, `.${ROOT_CLASS},\n.${ROOT_CLASS} * {`)
    .replace(/\bbody\s*\{/, `.${ROOT_CLASS} {`)
    .replace(
      /a\s*,\s*div\s*,\s*span\s*\{/,
      `.${ROOT_CLASS} a,\n.${ROOT_CLASS} div,\n.${ROOT_CLASS} span {`
    )
    .replace(/(^|})\s*a\s*\{/m, `$1.${ROOT_CLASS} a{`)
    .replace(/:root\s*\{/, `.${ROOT_CLASS} {`)
    .replace(/100vw/g, '100%')
    .replace(/100vh/g, '100%');

const extractBodyHtml = (html) => {
  const withoutComments = html.replace(/<!--[\s\S]*?-->/g, '');
  const match = withoutComments.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const body = match ? match[1] : withoutComments;
  return body.replace(/<script[\s\S]*?<\/script>/gi, '').trim();
};

const ensureCss = () => {
  if (typeof document === 'undefined') return Promise.resolve();
  if (cssPromise) return cssPromise;

  cssPromise = fetch(`${CDN_FILES}/calclayout.css`)
    .then((res) => {
      if (!res.ok) throw new Error('Failed to load calculator CSS');
      return res.text();
    })
    .then((css) => {
      let style = document.getElementById(CSS_ID);
      if (!style) {
        style = document.createElement('style');
        style.id = CSS_ID;
        document.head.appendChild(style);
      }
      style.textContent = scopeCalculatorCss(css);
    })
    .catch((err) => {
      cssPromise = null;
      throw err;
    });

  return cssPromise;
};

const ensureJquery = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.__UA_CALC_JQUERY) {
    window.$ = window.__UA_CALC_JQUERY;
    window.jQuery = window.__UA_CALC_JQUERY;
    return Promise.resolve();
  }
  if (jqueryPromise) return jqueryPromise;

  jqueryPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${CDN_FILES}/jquery-1.js`;
    script.async = false;
    script.onload = () => {
      window.__UA_CALC_JQUERY = window.jQuery;
      window.$ = window.jQuery;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  }).catch((err) => {
    jqueryPromise = null;
    throw err;
  });

  return jqueryPromise;
};

const loadCalcEngine = () =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${CDN_FILES}/oscZenoedited.js?mount=${Date.now()}`;
    script.async = false;
    script.onload = () => {
      script.remove();
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

const fetchMarkup = async (isScientific) => {
  const key = isScientific ? 'scientific' : 'normal';
  if (markupCache[key]) return markupCache[key];

  const res = await fetch(`${CDN_BASE}/${key}.html`);
  if (!res.ok) throw new Error('Failed to load calculator markup');
  const html = extractBodyHtml(await res.text());
  markupCache[key] = html;
  return html;
};

const InPageCalculator = forwardRef(({ isScientific, className }, ref) => {
  const rootRef = useRef(null);

  useImperativeHandle(ref, () => rootRef.current);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let cancelled = false;

    const onAnchorClick = (event) => {
      const anchor = event.target.closest?.('a');
      if (anchor && root.contains(anchor)) event.preventDefault();
    };
    root.addEventListener('click', onAnchorClick);

    const mount = async () => {
      await ensureCss();
      const html = await fetchMarkup(isScientific);
      if (cancelled || !rootRef.current) return;

      rootRef.current.innerHTML = html;
      await ensureJquery();
      if (cancelled || !rootRef.current) return;
      await loadCalcEngine();
    };

    mount().catch(() => {});

    return () => {
      cancelled = true;
      root.removeEventListener('click', onAnchorClick);
      root.innerHTML = '';
    };
  }, [isScientific]);

  return (
    <Root
      ref={rootRef}
      className={`${ROOT_CLASS}${className ? ` ${className}` : ''}`}
    />
  );
});

InPageCalculator.displayName = 'InPageCalculator';

export default InPageCalculator;

InPageCalculator.propTypes = {
  isScientific: PropTypes.bool,
  className: PropTypes.string
};

InPageCalculator.defaultProps = {
  isScientific: false,
  className: ''
};
